import axios, { AxiosRequestConfig } from 'axios';
import { Dispatch } from 'redux';
import { msalFetch } from '../components/auth/Auth-Utils';
import API from '../constants/apiEndpoints';
import { IdashboardData, IdashboardDataParams, Ijob, ThunkResult } from '../models';
import * as types from './actionTypes';
import { beginAjaxCall, endAjaxCall } from './ajaxStatusActions';
import { constants } from '../constants/constants';
import { downloadJobV2, setIsDownloadingJob } from './commonActions';
import { jobStatusEnum } from '../models-enums';
import { updateJob } from './manageJobActions';
import { isJobReadOnlyForUser } from '../utils/reviewFlow';

export function getFSEDashboard(params: IdashboardDataParams, cancelToken?: ReturnType<typeof axios.CancelToken.source>['token']): ThunkResult<any> {
    return async (dispatch, getState) => {
        const axiosOptions: AxiosRequestConfig = { method: 'get', params, ...(cancelToken ? { cancelToken } : {}) };
        const url = API.job.getFSEDashboard;
        dispatch(beginAjaxCall());
        try {
            const response = await msalFetch(url, axiosOptions);
            dispatch(endAjaxCall());
            const data = response?.data as IdashboardData;
            if (data) {
                dispatch({ type: types.GET_FSEDASHBOARD, payload: data });
            }
        } catch (error) {
            dispatch(endAjaxCall());
            if (!axios.isCancel(error)) {
                console.error(error);
            }
        }
    };
}

export async function getFSEAvailableJobsForWorkOrders(dispatch: Dispatch, workOrderIDs: Array<string>) {
    const url = API.job.getFSEAvailableJobsForWorkOrders;
    const axiosOptions: AxiosRequestConfig = {
        url,
        method: 'get',
        params: { workOrders: workOrderIDs.join(',') }
    };
    dispatch(beginAjaxCall());
    try {
        const response = await msalFetch(url, axiosOptions);
        return response?.data || [];
    } finally {
        dispatch(endAjaxCall());
    }
}

export async function getFSEAvailableJobTypesForWorkOrders(disaptch: Dispatch, wordOrderIds: Array<string>) {
    const url = API.job.getFSEAvailableJobTypesForWorkOrders;
    const axiosOptions: AxiosRequestConfig = { url, method: 'get', params: { workOrders: wordOrderIds.join(',') } };
    disaptch(beginAjaxCall());
    try {
        const response = await msalFetch(url, axiosOptions);
        return response?.data || [];
    } finally {
        disaptch(endAjaxCall());
    }
}

export async function getFSEJob(dispatch: Dispatch, id: string) {
    const url = `${API.job.single}/${id}`;
    const axiosOptions: AxiosRequestConfig = { method: 'get' };
    dispatch(beginAjaxCall());
    try {
        const response = await msalFetch(url, axiosOptions);
        return response?.data;
    } finally {
        dispatch(endAjaxCall());
    }
}

/*
 * Resolves true when the job was opened (download started and will navigate), false when it was not.
 * Callers must react to false: this runs immediately after CreateJob in the "Start Job" flow, so a
 * silent failure here leaves the FSE on the dashboard with no toast and no navigation - identical to
 * a tap that never registered. They retry, and the retry creates a SECOND job on the same work order.
 */
export function openFSEJob(id: string, history: any): ThunkResult<any> {
    return async (dispatch, getState) => {
        const url = `${API.job.single}/${id}`;
        const axiosOptions: AxiosRequestConfig = { method: 'get' };
        dispatch(beginAjaxCall());
        try {
            const response = await msalFetch(url, axiosOptions);
            const job = response?.data as Ijob;
            dispatch(endAjaxCall());
            if (!job) {
                constants.handleError(new Error('No job returned'), 'open the job');
                return false;
            }
            dispatch({ type: types.JOB_GET_ASSIGNED_SUCCESS, payload: { data: { job } }, includeCompleted: true });
            dispatch({ type: types.SET_SELECTED_JOB, job });
            const reopen =
                !job.isSAPClosed && job.status === jobStatusEnum.completed && !isJobReadOnlyForUser(job, getState().user);
            if (reopen) dispatch(updateJob({ ...job, status: jobStatusEnum.reopened }));
            dispatch(setIsDownloadingJob(true));
            dispatch(downloadJobV2(job, history)); // sending history will nav to the devices when finished downloading
            return true;
        } catch (error) {
            dispatch(endAjaxCall());
            console.error('[openFSEJob]:', error);
            constants.handleError(error, 'open the job');
            return false;
        }
    };
}
